// src/hooks/Web3.tsx
"use client";

import React, {
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useState
} from "react";
import type { Signer } from "ethers";
import { ethers } from "ethers";
import {
  useAccount,
  useChainId,
  usePublicClient,
  useWalletClient,
  useDisconnect,
  WagmiProvider
} from "wagmi";
import {
  createAppKit,
  useAppKit,
  useAppKitState
} from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { mainnet, sepolia } from "@reown/appkit/networks";
import { toast } from "react-toastify";
import { useLanguage } from "@/utils/LanguageContext";
import {
  createUserRejectedRequestError,
  getWalletErrorMessages,
  isWalletCancellationMessage,
  isWalletUserRejectedError
} from "@/utils/walletErrors";

/* ----------------------------
   AppKit Config
----------------------------- */
const projectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID!;
const baseUrlEnv = (process.env.NEXT_PUBLIC_BASE_URL || "https://invtron.com").replace(/\/$/, "");
const runtimeUrl = typeof window !== "undefined" ? window.location.origin : baseUrlEnv;
const ENV_CHAIN_ID = Number(process.env.NEXT_PUBLIC_CHAIN_ID ?? "1");

// Keep universal redirect unless you own a native scheme (custom protocol).
const metadata = {
  name: "Invtron DAO",
  description: "Invtron dApp",
  url: runtimeUrl,                        // must be https in production
  icons: [`${runtimeUrl}/favicon.png`],
  redirect: { universal: runtimeUrl }
};

const chainForEnv = ENV_CHAIN_ID === sepolia.id ? sepolia : mainnet;
const networks = [chainForEnv] as [typeof chainForEnv];

declare global {
  var __APPKIT_MODAL__: ReturnType<typeof createAppKit> | undefined;
  var __APPKIT_QUERY_CLIENT__: QueryClient | undefined;
  var __APPKIT_WAGMI_ADAPTER__: WagmiAdapter | undefined;
  var __APPKIT_CONSOLE_ERROR_RESTORE__: (() => void) | undefined;
}

if (!globalThis.__APPKIT_WAGMI_ADAPTER__) {
  globalThis.__APPKIT_WAGMI_ADAPTER__ = new WagmiAdapter({
    networks,
    projectId,
    // Keep wagmi hydration in an effect instead of during render.
    ssr: true
  });
}
export const wagmiAdapter = globalThis.__APPKIT_WAGMI_ADAPTER__;
export const wagmiConfig = wagmiAdapter.wagmiConfig;

if (!globalThis.__APPKIT_MODAL__) {
  globalThis.__APPKIT_MODAL__ = createAppKit({
    adapters: [wagmiAdapter],
    projectId,
    networks,
    metadata,
    enableAuthLogger: false,
    themeVariables: {
      "--w3m-z-index": 105000,
      // Prevent AppKit from injecting Reown's remote KHTeka font preload on routes
      // that never open the wallet modal, such as the flipbook/whitepaper pages.
      "--apkt-font-family": "var(--tg-body-font-family)"
    },
    features: {
      analytics: false
    }
  });
}
export const modal = globalThis.__APPKIT_MODAL__;

if (!globalThis.__APPKIT_QUERY_CLIENT__) {
  globalThis.__APPKIT_QUERY_CLIENT__ = new QueryClient();
}
export const queryClient = globalThis.__APPKIT_QUERY_CLIENT__;

function isWalletConnectConsoleNoise(args: unknown[]) {
  if (args.length === 2) {
    const [contextArg, messageArg] = args;
    if (
      contextArg &&
      Object.prototype.toString.call(contextArg) === "[object Object]" &&
      Object.getPrototypeOf(contextArg) === Object.prototype &&
      (contextArg as Record<string, unknown>).context === "client" &&
      messageArg === "eth_sendTransaction error"
    ) {
      return true;
    }

    if (typeof contextArg === "string" && isWalletConnectConsoleNoise([messageArg])) {
      return true;
    }
    return false;
  }

  if (args.length !== 1) return false;

  const [payload] = args;
  if (isWalletUserRejectedError(payload, "eth_sendTransaction")) {
    return true;
  }

  const extractedMessages = new Set<string>();
  if (typeof payload === "string" && payload.trim()) {
    extractedMessages.add(payload.trim());
  }
  for (const message of getWalletErrorMessages(payload)) {
    if (message.trim()) {
      extractedMessages.add(message.trim());
    }
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const directMessageKeys = ["msg", "message", "shortMessage", "details", "reason"];
    for (const key of directMessageKeys) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) {
        extractedMessages.add(value.trim());
      }
    }
  }

  for (const message of extractedMessages) {
    if (
      message.startsWith("onRelayMessage() -> failed to process an inbound") ||
      isWalletCancellationMessage(message, "eth_sendTransaction")
    ) {
      return true;
    }
  }

  if (!payload || typeof payload !== "object") {
    return false;
  }

  const prototype = Object.getPrototypeOf(payload);
  const record = payload as Record<string, unknown>;
  const ownKeys = Object.keys(record);
  const ownSymbols = Object.getOwnPropertySymbols(record);
  const ownPropertyNames = Object.getOwnPropertyNames(record);

  if (ownKeys.length === 0 && ownPropertyNames.length === 0 && ownSymbols.length === 0) {
    return true;
  }

  const stack = typeof record.stack === "string" ? record.stack : "";
  const message = typeof record.message === "string" ? record.message.trim() : "";
  const hasWalletConnectStack =
    stack.includes("@walletconnect") ||
    stack.includes("@reown") ||
    stack.includes("onRelayMessage");

  if (
    ownKeys.length === 0 &&
    ownSymbols.length === 0 &&
    ownPropertyNames.every((name) =>
      new Set(["message", "stack", "name", "cause", "code"]).has(name)
    ) &&
    (!message || isWalletCancellationMessage(message, "eth_sendTransaction")) &&
    hasWalletConnectStack
  ) {
    return true;
  }

  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }

  if (typeof record.time !== "number" || record.level !== 50) {
    return false;
  }

  const allowedKeys = new Set([
    "context",
    "level",
    "msg",
    "message",
    "name",
    "stack",
    "time",
    "type"
  ]);
  if (ownKeys.some((key) => !allowedKeys.has(key))) {
    return false;
  }

  return (
    ownKeys.every((key) =>
      key === "context" || key === "time" || key === "level" || allowedKeys.has(key)
    ) &&
    (extractedMessages.size === 0 ||
      [...extractedMessages].some((message) =>
        message.startsWith("onRelayMessage() -> failed to process an inbound")
      ))
  );
}

function installWalletConnectConsoleFilter() {
  if (
    process.env.NODE_ENV !== "development" ||
    typeof window === "undefined" ||
    globalThis.__APPKIT_CONSOLE_ERROR_RESTORE__
  ) {
    return;
  }

  const originalConsoleError = console.error.bind(console);
  const filteredConsoleError: typeof console.error = (...args) => {
    // Next dev overlays these WalletConnect relay logger payloads as runtime errors,
    // but they are internal transport noise rather than actionable app failures.
    if (isWalletConnectConsoleNoise(args)) {
      return;
    }

    originalConsoleError(...args);
  };

  console.error = filteredConsoleError;
  globalThis.__APPKIT_CONSOLE_ERROR_RESTORE__ = () => {
    if (console.error === filteredConsoleError) {
      console.error = originalConsoleError;
    }
    globalThis.__APPKIT_CONSOLE_ERROR_RESTORE__ = undefined;
  };
}

/* ----------------------------
   Helpers: viem → ethers (no viem/ethers import)
----------------------------- */

// Build a stable read-only Ethers provider from wagmi/viem publicClient
function getHttpUrlFromPublicClient(publicClient: any): string | null {
  if (!publicClient?.transport) return null;
  const t = publicClient.transport as any;
  // http transport exposes .url; fallback tries .urls[0]
  if (typeof t.url === "string") return t.url;
  if (Array.isArray(t.urls) && t.urls.length > 0) return t.urls[0];

  // Fallback to chain RPC URLs when transport lacks a direct URL (mobile)
  const rpcUrl =
    publicClient.chain?.rpcUrls?.public?.http?.[0] ??
    publicClient.chain?.rpcUrls?.default?.http?.[0];
  return rpcUrl ?? null;
}

// Build an EIP-1193 provider shim from walletClient (works for WalletConnect on mobile)
function eip1193FromWalletClient(walletClient: any) {
  if (!walletClient) return null;
  return {
    // EIP-1193 request interface
    request: async (args: { method: string; params?: any[] }) => {
      try {
        return await walletClient.request(args as any);
      } catch (error) {
        if (isWalletUserRejectedError(error, args.method)) {
          throw createUserRejectedRequestError(error);
        }
        throw error;
      }
    }
  };
}

/* ----------------------------
   Wallet Hook
----------------------------- */
const DESIRED_CHAIN_ID = ENV_CHAIN_ID;
const TOAST_IDS = {
  connectFail: "wallet-connect-fail",
  switchWarn: "wallet-switch-warn",
  switchFail: "wallet-switch-fail",
  wrongChain: "wallet-wrong-chain"
} as const;

function toastOnce(
  id: string,
  kind: "error" | "warn" | "info" | "success",
  msg: string
) {
  if (!toast.isActive(id)) toast[kind](msg, { toastId: id });
}

type WalletStatus = "idle" | "connecting" | "reconnecting" | "connected" | "chain-mismatch";

export function useWallet() {
  const { t } = useLanguage();
  const { address, isConnected, status: wagmiStatus } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();        // viem public client (read)
  const { data: walletClient } = useWalletClient(); // viem wallet client (write/sign)
  const { open } = useAppKit();
  const { open: walletModalOpen } = useAppKitState();
  const { disconnect } = useDisconnect();

  const chainMismatch =
    isConnected && typeof chainId === "number" && chainId !== DESIRED_CHAIN_ID;

  const status: WalletStatus = useMemo(() => {
    if (!isConnected) {
      if (wagmiStatus === "connecting") return "connecting";
      if (wagmiStatus === "reconnecting") return "reconnecting";
      return "idle";
    }
    return chainMismatch ? "chain-mismatch" : "connected";
  }, [isConnected, wagmiStatus, chainMismatch]);

  useEffect(() => {
    if (status === "chain-mismatch") {
      toastOnce(
        TOAST_IDS.wrongChain,
        "warn",
        t("You're connected to the wrong network. Please switch networks in your wallet.")
      );
    } else {
      toast.dismiss(TOAST_IDS.wrongChain);
    }
  }, [status, t]);

  // No storage/cookie wiping — that breaks WalletConnect pairing on mobile.
  const connect = useCallback(async () => {
    if (status === "connecting" || status === "connected") return;
    try {
      await open({ view: "Connect" } as any);
    } catch (err) {
      if (isWalletConnectConsoleNoise([err])) {
        return;
      }
      console.error("[useWallet] open() failed:", err);
      toastOnce(TOAST_IDS.connectFail, "error", t("Connection failed. Please try again."));
    }
  }, [open, status, t]);

  const switchToDesired = useCallback(async () => {
    if (!walletClient) {
      toastOnce(TOAST_IDS.switchFail, "error", t("No wallet found. Please connect first."));
      return;
    }
    try {
      await walletClient.switchChain({ id: DESIRED_CHAIN_ID });
      toast.dismiss(TOAST_IDS.switchWarn);
      toast.dismiss(TOAST_IDS.wrongChain);
    } catch {
      toastOnce(TOAST_IDS.switchWarn, "warn", t("Please switch networks in your wallet to continue."));
    }
  }, [walletClient, t]);

  // Read provider (Ethers) from viem public client
  const ethersProvider = useMemo(() => {
    const url = getHttpUrlFromPublicClient(publicClient);
    return url ? new ethers.JsonRpcProvider(url) : null;
  }, [publicClient]);

  // Signer from walletClient via EIP-1193 shim
  const [signer, setSigner] = useState<Signer | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!walletClient) {
          if (!cancelled) setSigner(null);
          return;
        }
        const eip1193 = eip1193FromWalletClient(walletClient);
        if (!eip1193) {
          if (!cancelled) setSigner(null);
          return;
        }
        const browserProvider = new ethers.BrowserProvider(
          eip1193 as any,
          walletClient.chain?.id
        );
        const s = await browserProvider.getSigner(walletClient.account?.address);
        if (!cancelled) setSigner(s as unknown as Signer);
      } catch {
        if (!cancelled) setSigner(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [walletClient]);

  const networkMessage =
    status === "chain-mismatch" ? t("Please switch to the supported network.") : null;

  return {
    address: isConnected ? address ?? null : null,
    chainId: typeof chainId === "number" ? chainId : null,
    publicClient,
    ethersProvider,   // read-only provider
    signer,           // signer for writes
    status,
    chainMismatch,
    networkMessage,
    walletModalOpen,
    connect,
    switchToDesired,
    disconnect
  };
}

/* ----------------------------
   Provider Component
----------------------------- */
export function Web3ModalProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    installWalletConnectConsoleFilter();

    return () => {
      globalThis.__APPKIT_CONSOLE_ERROR_RESTORE__?.();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <WagmiProvider config={wagmiConfig}>{children}</WagmiProvider>
    </QueryClientProvider>
  );
}
